import React, { useState, useEffect, ReactElement, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import type { AnyAction } from 'redux';
import { bindActionCreators } from 'redux';
import Notification from '../../../Common/Notification';
import * as types from '../../../../constants/actionTypes';
import { actions as baselineTaskActions } from '../../../../actions/baselineTaskActions';
import { actions as filesActions } from '../../../../actions/filesActions';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import InputText from '../../../Common/InputText';
import TextArea from '../../../Common/TextArea';
import { LinksComponentForm } from './LinksComponentForm';
import { AdditionalOptionsComponentForm } from './AdditionalOptionsComponentForm';
import { DependenciesComponentForm } from './DependenciesComponentForm';
import _ from 'lodash';
import { Calendar } from 'primereact/calendar';
import { DATE_FORMAT } from '../../../../constants/constants';
import Button from '@Common/Button';
import './CreateTaskForm.scss';
import { StepsComponentForm } from './StepsComponentForm';
import { CreateTaskProps, TaskPrereqSearchResultsResponse } from '../../Interfaces/PropTypes';
import Moment from 'moment';
import DrawerV6 from '@Components/Common/Drawer/DrawerV6/DrawerV6';
import { ThunkDispatch } from 'redux-thunk';

const CreateTaskForm = ({
    currentTask,
    isFormValid,
    onFormChange,
    checkForChanges,
    formFilters,
    baselineId,
    showDateError,
    taskFormMode,
    saveClick,
    cancelClick,
    isFromProject = false,
}: CreateTaskProps): ReactElement => {
    const [showSections, setShowSections] = useState({
        taskDetailsSection: true,
        timingSection: true,
        linkSection: false,
        dependenciesSection: false,
        additionalOptionsSection: false,
        stepsSection: false,
    });

    const [taskPrereqSearchResults, setTaskPrereqSearchResults] = useState<TaskPrereqSearchResultsResponse>({
        results: [],
    });

    const [taskSearchResults, setTaskSearchResults] = useState<TaskPrereqSearchResultsResponse>({
        results: [],
    });

    const [prereqTasksState, setPrereqTasksState] = useState<{ id: number; name: string }[]>(
        (currentTask.prereqTasks as { id: number; name: string }[]) || []
    );

    const [contingentState, setContingentState] = useState<{ id: number | string | null; name: string | null }>({
        id: (currentTask as any).contingentId ?? null,
        name: (currentTask as any).contingentName ?? null,
    });

    const [showValidation, setShowValidation] = useState(false);

    useEffect(() => {
        setShowValidation(false);
        setShowSections({
            taskDetailsSection: true,
            timingSection: true,
            linkSection: false,
            dependenciesSection: false,
            additionalOptionsSection: false,
            stepsSection: false,
        });
    }, [currentTask.id]);

    const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();


    // Bind filesActions to dispatch
    const boundFilesActions = bindActionCreators(filesActions, dispatch);

    const searchOptionPrereqTasks = async (searchTerm: string, baselineId: number, taskId: number, isPrereqQuery: boolean) =>
        await dispatch(baselineTaskActions.optionSearch(searchTerm, baselineId, taskId, isPrereqQuery));
    const onPrereqChangeDispatch = async (prereqId: number) => await dispatch(baselineTaskActions.updateContingent(prereqId, currentTask.id));
    const onRemovePrereqDispatch = async (prereqId: number) => await dispatch(baselineTaskActions.updateContingent(prereqId, null));
    const onUpdateContingentDispatch = async (contingentId: number) => await dispatch(baselineTaskActions.updateContingent(currentTask.id, contingentId));
    const onContingentRemoveDispatch = async (contingentId: number | null) => await dispatch(baselineTaskActions.updateContingent(currentTask.id, null));

    // Helper function to calculate target days out from weeks and days
    const calculateTargetDaysOut = (weeks, days) => {
        return (parseInt(weeks?.toString()) || 0) * 7 + (parseInt(days?.toString()) || 0);
    };

    // Helper function to filter tasks based on date comparison
    const filterTasksByDate = (tasks, isPrerequisiteSearch = true) => {
        return tasks.filter((task) => {
            // If current task has static dates set, compare with static dates
            if (currentTask.showStaticDates && currentTask.targetStaticDate) {
                const currentTargetDate = new Date(currentTask.targetStaticDate);

                // If the task also has static date, compare them
                if (task.targetStaticDate) {
                    const taskTargetDate = new Date(task.targetStaticDate);
                    return isPrerequisiteSearch
                        ? taskTargetDate <= currentTargetDate // Prerequisites: due before or on current task
                        : taskTargetDate >= currentTargetDate; // Contingents: due on or after current task
                }

                // If task has different date type or no date, allow it (let user decide)
                return true;
            }

            // If current task has relative dates set, compare with relative dates
            if (!currentTask.showStaticDates && (currentTask.targetWeeks || currentTask.targetDays)) {
                const currentTargetDaysOut = calculateTargetDaysOut(currentTask.targetWeeks, currentTask.targetDays);

                // If the task also has relative date, compare them
                if (task.targetDaysOut != null && task.targetDaysOut !== undefined) {
                    return isPrerequisiteSearch
                        ? task.targetDaysOut < currentTargetDaysOut // Prerequisites: due before current task
                        : task.targetDaysOut > currentTargetDaysOut; // Contingents: due after current task
                }

                // If task has different date type or no date, allow it (let user decide)
                return true;
            }

            // If current task doesn't have target date set yet, show all tasks
            return true;
        });
    };

    const searchPrereqTasks = async (searchTerm) => {
        try {
            const response = await searchOptionPrereqTasks(searchTerm, baselineId, currentTask.id, true);
            if (response.type === types.OPTION_SEARCH_BASELINE_TASK_SUCCESS) {
                const filteredResults = filterTasksByDate(response.payload.results, true);
                setTaskPrereqSearchResults({ results: filteredResults });
            } else if (response.type === types.OPTION_SEARCH_BASELINE_TASK_FAILURE) {
                Notification('error', 'An Error Occurred', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const searchTasks = async (searchTerm) => {
        try {
            const response = await searchOptionPrereqTasks(searchTerm, baselineId, currentTask.id, false);
            if (response.type === types.OPTION_SEARCH_BASELINE_TASK_SUCCESS) {
                const filteredResults = filterTasksByDate(response.payload.results, false);
                setTaskSearchResults({ results: filteredResults });
            } else if (response.type === types.OPTION_SEARCH_BASELINE_TASK_FAILURE) {
                Notification('error', 'An Error Occurred', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const onPrereqChange = async (prereqId: number) => {
        try {
            const response = await onPrereqChangeDispatch(prereqId);
            if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_SUCCESS) {
                Notification('success', '', 'Prereq Added');
                let prereqTasks: { id: number; name: string }[] = [];
                if (prereqId) {
                    const task = taskPrereqSearchResults.results.find((x) => x.id === prereqId);
                    if (task) {
                        prereqTasks = [...((currentTask.prereqTasks as { id: number; name: string }[]) || []), { id: task.id, name: task.name }];
                    } else {
                        prereqTasks = [...((currentTask.prereqTasks as { id: number; name: string }[]) || [])];
                    }
                }
                currentTask.prereqTasks = prereqTasks;
                onFormChange('prereqTasks', prereqTasks);
                setPrereqTasksState(prereqTasks);
                checkForChanges();
            } else if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const onRemovePrereq = async (prereqId: number) => {
        try {
            const response = await onRemovePrereqDispatch(prereqId);
            if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_SUCCESS) {
                Notification('success', '', 'Prereq Removed');
                const index = ((currentTask.prereqTasks as { id: number; name: string }[]) || []).findIndex((t) => t.id === prereqId);
                const prereqTasks = ((currentTask.prereqTasks as { id: number; name: string }[]) || []).slice();
                if (index > -1) {
                    prereqTasks.splice(index, 1);
                }
                currentTask.prereqTasks = prereqTasks;
                onFormChange('prereqTasks', prereqTasks);
                setPrereqTasksState(prereqTasks);
                checkForChanges();
            } else if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const handleOnFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        (currentTask as any)[name] = value;
        onFormChange(name, value);
    };

    const onAddStaticDates = () => {
        // Use the previous value to determine the new value and what to clear
        const prevShowStaticDates = currentTask.showStaticDates;
        const newShowStaticDates = !prevShowStaticDates;

        if (newShowStaticDates) {
            // Switching to static dates: clear relative fields
            onFormChange('showStaticDates', true);
            onFormChange('startDays', 0);
            onFormChange('startWeeks', 0);
            onFormChange('targetDays', 0);
            onFormChange('targetWeeks', 0);
        } else {
            // Switching to relative dates: clear static fields
            onFormChange('showStaticDates', false);
            onFormChange('startStaticDate', '');
            onFormChange('targetStaticDate', '');
        }
    };

    const onDatePickerChange = (date: Date | null | undefined, name: string) => {
        if (!date) return;
        if (name === 'targetStaticDate' || name === 'startStaticDate') {
            var dateFormatted = Moment.utc(date).format(DATE_FORMAT);
            (currentTask as any)[name] = dateFormatted;
        } else {
            (currentTask as any)[name] = date;
        }
        onFormChange(name, (currentTask as any)[name]);
    };

    const handleOnSubmit = (e) => {
        e.preventDefault();
    };

    const onDropdownChange = (value: number, name: string) => {
        (currentTask as any)[name] = value;
        onFormChange(name, value);
    };

    const onMakeMilestone = () => {
        currentTask.isMilestone = !currentTask.isMilestone;
        onFormChange('isMilestone', currentTask.isMilestone);
    };

    const onClickLinkSection = (e, sectionName) => {
        e.preventDefault();

        setShowSections((prevShowSections) => ({
            ...prevShowSections,
            [sectionName]: !prevShowSections[sectionName],
        }));
    };

    const onContingentChange = async (contingentId: number, contingentName: string) => {
        if (contingentId === currentTask.id) {
            Notification('error', 'Invalid Contingent', 'You can not make this task a contingent upon itself.');
            return;
        }

        currentTask.contingentId = contingentId;
        currentTask.contingentName = contingentName;
        onFormChange('contingentId', currentTask.contingentId);
        onFormChange('contingentName', currentTask.contingentName);

        try {
            const response = await onUpdateContingentDispatch(contingentId);
            if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_SUCCESS) {
                Notification('success', '', 'Contingent Updated');
                currentTask.contingentId = contingentId;
                currentTask.contingentName = contingentName;
                onFormChange('contingentId', currentTask.contingentId);
                onFormChange('contingentName', currentTask.contingentName);
                setContingentState({ id: contingentId, name: contingentName });
                checkForChanges();
            } else if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
            (currentTask as any).markAsDependent = false;
            onFormChange('markAsDependent', (currentTask as any).markAsDependent);
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const onContingentRemove = async (contingentId: number | null) => {
        try {
            const response = await onContingentRemoveDispatch(contingentId);
            if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_SUCCESS) {
                Notification('success', '', 'Dependency Removed');
                currentTask.contingentId = null;
                currentTask.contingentName = null;
                onFormChange('contingentId', null);
                onFormChange('contingentName', null);
                setContingentState({ id: null, name: null });
                checkForChanges();
            } else if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const milestoneLinkText = currentTask.isMilestone ? 'Change to Task' : 'Change to Milestone';
    const milestoneInputText = currentTask.isMilestone ? 'Milestone Task Name' : 'Baseline Task Name';

    const handleCancelClick = () => {
        setShowValidation(false);
        cancelClick();
    };

    const handleSaveClick = (createNew: boolean) => {
        setShowValidation(true);
        return saveClick(createNew);
    };

    return (
        <DrawerV6
            formButtons={taskFormMode === 'create' ? [<Button
                key={1}
                type='button'
                bootstrapClass='drawer-button btn btn-primary button--save'
                onClick={() => handleSaveClick(false)}
                text={'Save Task and Close'}
            />] : undefined}
            isWide
            title={taskFormMode == 'create' ? 'Create New Baseline Task' : 'Edit Baseline Task'}
            saveText={taskFormMode == 'create' ? 'Save Task and Create New' : 'Update Baseline Task'}
            onCancelClick={handleCancelClick}
            onSaveClick={() => handleSaveClick(taskFormMode === 'create')}>
            <div className='task-details-content col-12 px-0' id='accordion'>
                <div className='wizard-content-title d-flex align-items-center justify-content-between '>
                    Instructions
                    <button className='btn btn-link float-end' onClick={(e) => onClickLinkSection(e, 'taskDetailsSection')}>
                        - {showSections.taskDetailsSection ? 'Hide' : 'Show'} Task Details
                    </button>
                </div>
                <form name='newtaskWizardStep1Form' noValidate className={`needs-validation ${showValidation && !isFormValid ? 'was-validated' : ''}`} onSubmit={handleOnSubmit}>
                    {showSections.taskDetailsSection && (
                        <div className='row me-3' id='collapseLinks'>
                            <div className='col-md-6 col-12'>
                                <div className='mb-3'>
                                    <label htmlFor='name' className='title'>
                                        {milestoneInputText}
                                    </label>
                                    <InputText
                                        id='name'
                                        name='name'
                                        placeholder='What needs to be completed?'
                                        type='text'
                                        value={currentTask.name ?? ''}
                                        customClass='mb-1'
                                        onChange={handleOnFormChange}
                                        isRequired={true}
                                        errorMessage='Task Name is required'
                                        maxLength={175}
                                    />
                                    <div className='milestone'>
                                        <div className='row d-flex justify-content-between g-0 mt-0'>
                                            <div className='col'>
                                                <small id='task-name-help' className='form-text text-muted sub-title'>
                                                    Character Limit 175
                                                </small>
                                            </div>
                                            <div className='col text-end sub-title'>
                                                <button type='button' className='btn btn-link p-0' onClick={onMakeMilestone}>
                                                    {milestoneLinkText}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {isFromProject ? (
                                    <div className='mb-3'>
                                        <label htmlFor='name' className='title'>
                                            Baselines
                                        </label>
                                        <MultiSelectBox
                                            name='baselineIdsSelected'
                                            options={formFilters.baselineIdsFilter ?? []}
                                            idField='id'
                                            valueField='name'
                                            onChange={onDropdownChange}
                                            isRequired={isFromProject}
                                            errorMessage='Baselines are required'
                                            isMulti={true}
                                            placeholder='Which Baseline(s) should this task be added to?'
                                            selectedValues={currentTask?.baselineIdsSelected?.toString() ?? ''}
                                        />
                                    </div>
                                ) : (
                                    <div className='mb-3'>
                                        <label htmlFor='jobTitle' className='title'>
                                            Job Title
                                        </label>
                                        <MultiSelectBox
                                            name='jobTitleId'
                                            options={formFilters.baselineJobTitles ?? []}
                                            idField='id'
                                            valueField='name'
                                            selectedValues={
                                                (formFilters.baselineJobTitles ?? []).some(
                                                    (jt) => jt.id?.toString() === currentTask.jobTitleId?.toString()
                                                )
                                                    ? currentTask.jobTitleId?.toString() ?? ''
                                                    : ''
                                            }
                                            onChange={onDropdownChange}
                                            isRequired={isFromProject}
                                            errorMessage='Baseline Job Title is required'
                                            isMulti={false}
                                            placeholder='What role is usually responsible for completing this task?'
                                        />
                                    </div>
                                )}

                                <div className='mb-3'>
                                    <label htmlFor='departmentId' className='title'>
                                        Department
                                    </label>
                                    <MultiSelectBox
                                        name='departmentId'
                                        idField='id'
                                        valueField='name'
                                        options={[...(formFilters.subscriptionDepartments ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))}
                                        placeholder='What department is usually responsible for completing this task?'
                                        selectedValues={currentTask.departmentId?.toString() ?? ''}
                                        onChange={onDropdownChange}
                                        isRequired={true}
                                        errorMessage='Department is required'
                                        isMulti={false}
                                    />
                                </div>
                            </div>
                            <div className='col-md-6 col-12'>
                                <div className='mb-3'>
                                    <label htmlFor='task-description' className='title'>
                                        Description
                                    </label>
                                    <TextArea
                                        rows={3}
                                        id='additionalInfo'
                                        name='additionalInfo'
                                        placeholder="How should the task be completed and it's impact?"
                                        value={currentTask.additionalInfo ?? ''}
                                        onChange={handleOnFormChange}
                                        maxLength={5000}
                                    />
                                </div>

                                {isFromProject && (
                                    <div className='form-group'>
                                        <label htmlFor='jobTitle' className='title'>
                                            Job title
                                        </label>
                                        <MultiSelectBox
                                            name='jobTitleId'
                                            options={formFilters.baselineJobTitles ?? []}
                                            idField='id'
                                            valueField='name'
                                            selectedValues={
                                                (formFilters.baselineJobTitles ?? []).some(
                                                    (jt) => jt.id?.toString() === currentTask.jobTitleId?.toString()
                                                )
                                                    ? currentTask.jobTitleId?.toString() ?? ''
                                                    : ''
                                            }
                                            onChange={onDropdownChange}
                                            isRequired={true}
                                            errorMessage='Baseline Job Title is required'
                                            isMulti={false}
                                            placeholder='What role is usually responsible for completing this task?'
                                        />
                                    </div>
                                )}

                                <div className='mb-3'>
                                    <label htmlFor='communityId' className='title'>
                                        Community
                                    </label>
                                    <MultiSelectBox
                                        name='communityId'
                                        idField='id'
                                        valueField='name'
                                        options={formFilters.communities ?? []}
                                        placeholder='Which community of users should be able to see this task?'
                                        selectedValues={currentTask.communityId?.toString() ?? ''}
                                        onChange={onDropdownChange}
                                        isRequired={true}
                                        errorMessage='Community is required'
                                        isMulti={false}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className='wizard-step-line'></div>

                    {/* Dates */}
                    <br></br>
                    <div className='border-bottom flex-grow-1 me-3'></div>
                    <br></br>
                    <div className='wizard-content-title d-flex align-items-center justify-content-between me-3'>
                        Timing
                        <button
                            className='btn btn-link float-end'
                            data-toggle='collapse'
                            data-target='#collapseDates'
                            aria-expanded={showSections.timingSection}
                            aria-controls='collapseDates'
                            onClick={(e) => onClickLinkSection(e, 'timingSection')}>
                            - {showSections.timingSection ? 'Hide' : 'Show'} Task Dates
                        </button>
                    </div>
                    <div
                        className={`row me-3 collapse ${showSections.timingSection ? 'show' : ''}`}
                        id='collapseLinks'
                        aria-hidden={!showSections.timingSection}>
                        <div className='col-12'></div>
                        <div className='col-md-6 col-12'>
                            <label htmlFor='start-date' className='title'>
                                Start Date
                            </label>
                            <p>
                                {currentTask.showStaticDates
                                    ? 'When should this task start?'
                                    : 'When should this task start relative to the selected zero date?'}
                            </p>
                        </div>
                        <div className='col-md-6 col-12'>
                            <label htmlFor='target-date' className='title'>
                                Target Date
                            </label>
                            <p>
                                {currentTask.showStaticDates
                                    ? 'When should this task be complete?'
                                    : 'When should this task be complete relative to the selected zero date?'}
                            </p>
                        </div>
                        {currentTask.showStaticDates ? (
                            <Fragment>
                                <Fragment>
                                    <div className='col-md-6 col-12'>
                                        <Calendar
                                            value={currentTask?.startStaticDate ? new Date(Date.parse(currentTask.startStaticDate)) : null}
                                            className={`calendar-input w-100`}
                                            onChange={(e) => onDatePickerChange(e.value, 'startStaticDate')}
                                            placeholder=''
                                            dateFormat='dd M yy'
                                            name='startStaticDate'
                                        />
                                    </div>
                                    <div className='col-md-6 col-12'>
                                        <Calendar
                                            value={currentTask?.targetStaticDate ? new Date(Date.parse(currentTask.targetStaticDate)) : null}
                                            className={`calendar-input w-100 ${currentTask.showStaticDates &&
                                                (currentTask.targetStaticDate === null || currentTask.targetStaticDate === '')
                                                ? 'error'
                                                : ''
                                                }`}
                                            onChange={(e) => onDatePickerChange(e.value, 'targetStaticDate')}
                                            placeholder=''
                                            dateFormat='dd M yy'
                                            name='targetStaticDate'
                                        />
                                        {currentTask.showStaticDates &&
                                            (currentTask.targetStaticDate === null || currentTask.targetStaticDate === '') && (
                                                <div style={{ color: 'red', marginTop: '5px' }}>Target static date is required</div>
                                            )}
                                    </div>
                                </Fragment>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <div className='col-lg-3 col-md-6 col-12'>
                                    <div className='mb-3'>
                                        <label htmlFor='startWeeks' className='title'>
                                            Weeks
                                        </label>
                                        <InputText
                                            name='startWeeks'
                                            id='startWeeks'
                                            onChange={handleOnFormChange}
                                            isRequired={false}
                                            value={currentTask.startWeeks}
                                            type='number'
                                        />
                                    </div>
                                </div>
                                <div className='col-lg-3 col-md-6 col-12'>
                                    <div className='mb-3'>
                                        <label htmlFor='startDays' className='title'>
                                            Days
                                        </label>
                                        <InputText
                                            id='startDays'
                                            name='startDays'
                                            onChange={handleOnFormChange}
                                            isRequired={false}
                                            value={currentTask.startDays}
                                            type='number'
                                        />
                                    </div>
                                </div>
                                <div className='col-lg-3 col-md-6 col-12'>
                                    <div className='mb-3'>
                                        <label htmlFor='targetWeeks' className='title'>
                                            Weeks
                                        </label>
                                        <InputText
                                            id='targetWeeks'
                                            name='targetWeeks'
                                            onChange={handleOnFormChange}
                                            value={currentTask.targetWeeks}
                                            type='number'
                                            isRequired={!currentTask.showStaticDates}
                                            errorMessage='Target weeks required'
                                        />
                                    </div>
                                </div>
                                <div className='col-lg-3 col-md-6 col-12'>
                                    <div className='mb-3'>
                                        <label htmlFor='targetDays' className='title'>
                                            Days
                                        </label>
                                        <InputText
                                            id='targetDays'
                                            name='targetDays'
                                            onChange={handleOnFormChange}
                                            value={currentTask.targetDays}
                                            type='number'
                                            isRequired={!currentTask.showStaticDates}
                                            errorMessage='Target days required'
                                        />
                                    </div>
                                </div>
                            </Fragment>
                        )}
                        {showDateError ? (
                            <div className='alert alert-danger ms-3 mt-1' role='alert'>
                                Target date must be after Start Date
                            </div>
                        ) : (
                            ''
                        )}
                        <div className='col-12'>
                            <div className='what-is-link float-end'>
                                <button type='button' className='btn btn-link' onClick={onAddStaticDates}>
                                    <i className='fa-regular fa-info-circle fa-lg'></i>
                                    &nbsp;{currentTask.showStaticDates ? 'Change to Relative Dates' : 'Change to a Static Date'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* TASK LINKS */}
                    <br></br>
                    <div className='border-bottom flex-grow-1 me-3'></div>
                    <div className='wizard-content-title d-flex align-items-center justify-content-between me-3 pt-3'>
                        Links & Files ({currentTask?.links?.length ?? 0})
                        <button
                            className='btn btn-link float-end'
                            data-toggle='collapse'
                            data-target='#collapseLinks'
                            aria-expanded={showSections.linkSection}
                            aria-controls='collapseLinks'
                            onClick={(e) => onClickLinkSection(e, 'linkSection')}>
                            - {showSections.linkSection ? 'Hide' : 'Show'} Links & Files
                        </button>
                    </div>
                    <div
                        className={`row me-3 collapse ${showSections.linkSection ? 'show' : ''}`}
                        id='collapseLinks'
                        aria-hidden={!showSections.linkSection}>
                        <LinksComponentForm
                            baselineTask={currentTask}
                            onFormChange={onFormChange}
                            formFilters={formFilters as any}
                            currentTask={currentTask}
                            filesActions={boundFilesActions as any}></LinksComponentForm>
                    </div>
                    {/* DEPENDENCIES */}
                    {formFilters.isTaskDependenciesActive && (
                        <Fragment>
                            <br></br>
                            <div className='border-bottom flex-grow-1 me-3'></div>
                            <br></br>
                            <div className='wizard-content-title d-flex align-items-center justify-content-between me-3'>
                                Dependencies
                                <button
                                    className='btn btn-link float-end'
                                    data-toggle='collapse'
                                    data-target='#collapseDependencies'
                                    aria-expanded={showSections.dependenciesSection}
                                    aria-controls='collapseDependencies'
                                    onClick={(e) => onClickLinkSection(e, 'dependenciesSection')}>
                                    - {showSections.dependenciesSection ? 'Hide' : 'Show'} Dependencies
                                </button>
                            </div>
                            <div
                                className={`row me-3 collapse ${showSections.dependenciesSection ? 'show' : ''}`}
                                id='collapseDependencies'
                                aria-hidden={!showSections.dependenciesSection}>
                                <DependenciesComponentForm
                                    currentTask={currentTask}
                                    formFilters={formFilters}
                                    onFormChange={onFormChange}
                                    onContingentRemove={onContingentRemove}
                                    searchPrereqTasks={searchPrereqTasks}
                                    taskPrereqSearchResults={taskPrereqSearchResults?.results ?? []}
                                    onPrereqChange={onPrereqChange}
                                    onRemovePrereq={onRemovePrereq}
                                    searchTasks={searchTasks}
                                    taskSearchResults={taskSearchResults?.results ?? []}
                                    onContingentChange={onContingentChange}
                                    prereqTasks={prereqTasksState}
                                    contingentId={contingentState.id}
                                    contingentName={contingentState.name}
                                ></DependenciesComponentForm>
                            </div>
                        </Fragment>
                    )}
                    {/* STEPS */}
                    {formFilters.isTaskStepsActive && (
                        <Fragment>
                            <br></br>
                            <div className='border-bottom flex-grow-1 me-3'></div>
                            <br></br>
                            <div className='wizard-content-title d-flex align-items-center justify-content-between me-3'>
                                Additional Options
                                <button
                                    className='btn btn-link float-end'
                                    data-toggle='collapse'
                                    data-target='#collapseAdditionalOptions'
                                    aria-expanded={showSections.additionalOptionsSection}
                                    aria-controls='collapseAdditionalOptions'
                                    onClick={(e) => onClickLinkSection(e, 'additionalOptionsSection')}>
                                    - {showSections.additionalOptionsSection ? 'Hide' : 'Show'} additional Options
                                </button>
                            </div>
                            <div
                                className={`row me-3 collapse ${showSections.additionalOptionsSection ? 'show' : ''}`}
                                id='collapseAdditionalOptions'
                                aria-hidden={!showSections.additionalOptionsSection}>
                                <AdditionalOptionsComponentForm
                                    currentTask={currentTask}
                                    formFilters={formFilters}
                                    onFormChange={onFormChange}></AdditionalOptionsComponentForm>
                            </div>
                            <br></br>
                            <div className='border-bottom flex-grow-1 me-3'></div>
                            <br></br>
                            <div className='wizard-content-title d-flex align-items-center justify-content-between me-3'>
                                <div>
                                    Steps <i className='fa-regular fa-info-circle fa-lg'></i>
                                </div>
                                <button
                                    className='btn btn-link float-end'
                                    data-toggle='collapse'
                                    data-target='#collapseSteps'
                                    aria-expanded={showSections.stepsSection}
                                    aria-controls='collapseSteps'
                                    onClick={(e) => onClickLinkSection(e, 'stepsSection')}>
                                    - {showSections.stepsSection ? 'Hide' : 'Show'} Steps
                                </button>
                            </div>
                            <div
                                className={`row me-3 collapse ${showSections.stepsSection ? 'show' : ''}`}
                                id='collapseSteps'
                                aria-hidden={!showSections.stepsSection}>
                                <StepsComponentForm currentTask={currentTask} onFormChange={onFormChange}></StepsComponentForm>
                            </div>
                        </Fragment>
                    )}
                </form>
            </div>
        </DrawerV6>
    );
};

export default CreateTaskForm;
